import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, MarkerClusterer } from '@react-google-maps/api';
import { ConnectorMarkerData } from '../../pages/Dashboard/DashboardPage';
import { MapConnectorMarker } from '../../pages/Dashboard/MapConnectorMarker';
import { Marker } from '@react-google-maps/api';
import { Library } from '@googlemaps/js-api-loader';

export interface GoogleMapsWrapperProps {
  centerByMarkers?: boolean;
  pos: { lat: number; lng: number };
  zoom?: number;
  dragCallback?: (lat: number, lng: number) => void;
  showLocationMarker?: boolean;
  connectorMarkers?: ConnectorMarkerData[];
}

const libraries: Library[] = ['geometry', 'drawing', 'places'];

const GoogleMapsWrapper = ({
  centerByMarkers = false,
  pos,
  zoom = 8,
  dragCallback,
  showLocationMarker,
  connectorMarkers,
}: GoogleMapsWrapperProps) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries,
  });

  const location = useLocation();
  const isLocationPage = location.pathname.includes('/locations/');
  const defaultPos = !isNaN(pos.lat) && !isNaN(pos.lng) ? pos : undefined;

  const mapRef = useRef<google.maps.Map | null>(null);

  const onDragEnd = (e: google.maps.MapMouseEvent) => {
    if (dragCallback && mapRef.current) {
      const latLng = e.latLng;
      dragCallback(latLng.lat(), latLng.lng());
    }
  };

  useEffect(() => {
    if (connectorMarkers && centerByMarkers && mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      connectorMarkers.forEach((marker) => {
        bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
      });
      mapRef.current.fitBounds(bounds);
    }
  }, [centerByMarkers, connectorMarkers]);

  if (!isLoaded) {
    return <div>Loading Map...</div>;
  }

  return (
    <div
      style={
        isLocationPage
          ? {
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }
          : {
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'fixed',
              top: '60px',
              left: '260px',
            }
      }
    >
      <GoogleMap
        mapContainerStyle={
          isLocationPage
            ? { height: '100%', width: '100%', borderRadius: 16 }
            : { height: '100%', width: '100%', position: 'fixed' }
        }
        center={defaultPos}
        zoom={zoom}
        onLoad={(map) => {
          mapRef.current = map;
        }}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {showLocationMarker && defaultPos && (
          <Marker position={defaultPos} draggable onDragEnd={onDragEnd} />
        )}

        {connectorMarkers && connectorMarkers.length > 0 ? (
          <MarkerClusterer
            options={{
              minimumClusterSize: 2,
              gridSize: 60,
              maxZoom: 16, // optional: prevents zooming too far out while clustering
              imagePath: '/cluster-icons/m',
            }}
          >
            {(clusterer) =>
              connectorMarkers.map((data, index) => (
                <MapConnectorMarker
                  key={`${data.lat}_${data.lng}_${index}`}
                  data={data}
                  clusterer={clusterer}
                />
              ))
            }
          </MarkerClusterer>
        ) : null}
      </GoogleMap>
    </div>
  );
};

export default GoogleMapsWrapper;
